// OKLCH-based theme engine
// Color space: hex → RGB → linear RGB → OKLab → OKLCH and back

// ─── Color space conversion ───────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return [r, g, b];
}

function rgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function linearToRgb(channel: number): number {
  const c = channel <= 0.0031308 ? 12.92 * channel : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
  return Math.round(Math.max(0, Math.min(1, c)) * 255);
}

function rgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const lr = rgbToLinear(r);
  const lg = rgbToLinear(g);
  const lb = rgbToLinear(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  ];
}

function oklabToOklch(L: number, a: number, b: number): [number, number, number] {
  const C = Math.sqrt(a * a + b * b);
  const H = Math.atan2(b, a) * (180 / Math.PI);
  return [L, C, H < 0 ? H + 360 : H];
}

function oklchToOklab(L: number, C: number, H: number): [number, number, number] {
  const hRad = H * (Math.PI / 180);
  return [L, C * Math.cos(hRad), C * Math.sin(hRad)];
}

function oklabToRgb(L: number, a: number, b: number): [number, number, number] {
  const l = L + 0.3963377774 * a + 0.2158037573 * b;
  const m = L - 0.1055613458 * a - 0.0638541728 * b;
  const s = L - 0.0894841775 * a - 1.291485548 * b;

  const lr = l * l * l;
  const mr = m * m * m;
  const sr = s * s * s;

  const r = +4.0767416621 * lr - 3.3077115913 * mr + 0.2309699292 * sr;
  const g = -1.2684380046 * lr + 2.6097574011 * mr - 0.3413193965 * sr;
  const bv = -0.0041960863 * lr - 0.7034186147 * mr + 1.707614701 * sr;

  return [linearToRgb(r), linearToRgb(g), linearToRgb(bv)];
}

function oklchToHex(L: number, C: number, H: number): string {
  const [la, a, b] = oklchToOklab(L, C, H);
  const [r, g, bv] = oklabToRgb(la, a, b);
  return '#' + [r, g, bv].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function hexToHsl(hex: string): string {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// ─── Shade scale generation ───────────────────────────────────────────────────

export type ShadeScale = Record<string, string>;

const SHADE_CONFIGS: Array<{ key: string; L: number; C_factor: number }> = [
  { key: '50', L: 0.975, C_factor: 0.08 },
  { key: '100', L: 0.94, C_factor: 0.15 },
  { key: '200', L: 0.89, C_factor: 0.25 },
  { key: '300', L: 0.82, C_factor: 0.45 },
  { key: '400', L: 0.72, C_factor: 0.75 },
  { key: '500', L: 0.62, C_factor: 0.9 },
  { key: '600', L: 0.52, C_factor: 1.0 },
  { key: '700', L: 0.43, C_factor: 0.95 },
  { key: '800', L: 0.35, C_factor: 0.8 },
  { key: '900', L: 0.27, C_factor: 0.6 },
  { key: '950', L: 0.2, C_factor: 0.45 }
];

export function generateShadeScale(hexColor: string): ShadeScale {
  const [r, g, b] = hexToRgb(hexColor);
  const [L, a, bv] = rgbToOklab(r, g, b);
  const [, C, H] = oklabToOklch(L, a, bv);

  return SHADE_CONFIGS.reduce<ShadeScale>((acc, { key, L: targetL, C_factor }) => {
    acc[key] = oklchToHex(targetL, C * C_factor, H);
    return acc;
  }, {});
}

// ─── Theme presets ────────────────────────────────────────────────────────────

export type ThemePresetId =
  | 'default'
  | 'ocean'
  | 'emerald'
  | 'sunset'
  | 'rose'
  | 'violet'
  | 'graphite';
export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemePreset {
  readonly id: ThemePresetId;
  readonly label: string;
  readonly hex: string;
  readonly shades: ShadeScale;
}

const PRESET_HEX: Record<ThemePresetId, string> = {
  default: '#2563EB',
  ocean: '#06B6D4',
  emerald: '#10B981',
  sunset: '#F59E0B',
  rose: '#F43F5E',
  violet: '#8B5CF6',
  graphite: '#6B7280'
};

function buildPresets(): Record<ThemePresetId, ThemePreset> {
  const ids = Object.keys(PRESET_HEX) as ThemePresetId[];
  return ids.reduce<Record<ThemePresetId, ThemePreset>>(
    (acc, id) => {
      const hex = PRESET_HEX[id];
      acc[id] = {
        id,
        label: id.charAt(0).toUpperCase() + id.slice(1),
        hex,
        shades: generateShadeScale(hex)
      };
      return acc;
    },
    {} as Record<ThemePresetId, ThemePreset>
  );
}

export const THEME_PRESETS = buildPresets();

export const PRESET_DISPLAY_NAMES: Record<ThemePresetId, string> = {
  default: 'Default (Blue)',
  ocean: 'Ocean (Cyan)',
  emerald: 'Emerald',
  sunset: 'Sunset (Amber)',
  rose: 'Rose',
  violet: 'Violet',
  graphite: 'Graphite'
};

// ─── Neutral surface palettes ─────────────────────────────────────────────
// Surfaces are always neutral — only primary/ring/accent use brand color.
// This ensures consistent contrast regardless of which color preset is active.

const LIGHT_SURFACES = {
  background: '210 20% 98%', // #f8fafc — clean neutral
  foreground: '215 28% 17%', // #1e293b
  card: '0 0% 100%', // #ffffff
  cardForeground: '215 28% 17%', // #1e293b
  popover: '0 0% 100%', // #ffffff
  popoverForeground: '215 28% 17%', // #1e293b
  secondary: '210 20% 96%', // #f1f5f9
  secondaryForeground: '215 28% 17%', // #1e293b
  muted: '210 20% 96%', // #f1f5f9
  mutedForeground: '215 16% 47%', // #64748b
  border: '214 20% 89%', // #e2e8f0
  input: '214 20% 89%', // #e2e8f0
  destructive: '0 84% 60%',
  destructiveForeground: '0 0% 100%'
} as const;

const DARK_SURFACES = {
  background: '222 47% 5%', // #0b0f19 — deepest
  foreground: '210 40% 98%', // #f8fafc
  card: '221 39% 11%', // #111827 — subtle lift from bg
  cardForeground: '210 40% 98%', // #f8fafc
  popover: '221 39% 11%', // #111827
  popoverForeground: '210 40% 98%', // #f8fafc
  secondary: '217 33% 14%', // #182032 — hover/active states
  secondaryForeground: '210 40% 98%', // #f8fafc
  muted: '217 33% 14%', // #182032
  mutedForeground: '215 20% 65%', // #94a3b8
  border: '217 33% 14%', // #182032 — subtle on card
  input: '217 33% 16%', // #1a2540
  destructive: '0 63% 31%',
  destructiveForeground: '210 40% 98%'
} as const;

// ─── Apply theme ──────────────────────────────────────────────────────────────

const FONT_SIZES: Record<string, string> = {
  small: '14px',
  default: '16px',
  large: '18px'
};

export function applyFontSize(size: 'small' | 'default' | 'large'): void {
  document.documentElement.style.setProperty('font-size', FONT_SIZES[size] ?? '16px');
}

export function applyCompactMode(compact: boolean): void {
  if (compact) {
    document.documentElement.classList.add('compact');
  } else {
    document.documentElement.classList.remove('compact');
  }
}

export function applyTheme(presetId: ThemePresetId, customHex?: string, mode?: ThemeMode): void {
  const preset = THEME_PRESETS[presetId];
  const shades = customHex ? generateShadeScale(customHex) : preset.shades;

  const resolvedMode: 'light' | 'dark' =
    mode === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : (mode ?? 'light');

  const root = document.documentElement;

  if (resolvedMode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  if (resolvedMode === 'dark') {
    // Neutral surfaces — never brand-tinted
    root.style.setProperty('--background', DARK_SURFACES.background);
    root.style.setProperty('--foreground', DARK_SURFACES.foreground);
    root.style.setProperty('--card', DARK_SURFACES.card);
    root.style.setProperty('--card-foreground', DARK_SURFACES.cardForeground);
    root.style.setProperty('--popover', DARK_SURFACES.popover);
    root.style.setProperty('--popover-foreground', DARK_SURFACES.popoverForeground);
    root.style.setProperty('--secondary', DARK_SURFACES.secondary);
    root.style.setProperty('--secondary-foreground', DARK_SURFACES.secondaryForeground);
    root.style.setProperty('--muted', DARK_SURFACES.muted);
    root.style.setProperty('--muted-foreground', DARK_SURFACES.mutedForeground);
    root.style.setProperty('--border', DARK_SURFACES.border);
    root.style.setProperty('--input', DARK_SURFACES.input);
    root.style.setProperty('--destructive', DARK_SURFACES.destructive);
    root.style.setProperty('--destructive-foreground', DARK_SURFACES.destructiveForeground);
    // Brand-derived — only accent colors
    root.style.setProperty('--primary', hexToHsl(shades['400']));
    root.style.setProperty('--primary-foreground', '222 47% 5%');
    root.style.setProperty('--accent', hexToHsl(shades['400']));
    root.style.setProperty('--accent-foreground', '222 47% 5%');
    root.style.setProperty('--ring', hexToHsl(shades['400']));
  } else {
    // Neutral surfaces — never brand-tinted
    root.style.setProperty('--background', LIGHT_SURFACES.background);
    root.style.setProperty('--foreground', LIGHT_SURFACES.foreground);
    root.style.setProperty('--card', LIGHT_SURFACES.card);
    root.style.setProperty('--card-foreground', LIGHT_SURFACES.cardForeground);
    root.style.setProperty('--popover', LIGHT_SURFACES.popover);
    root.style.setProperty('--popover-foreground', LIGHT_SURFACES.popoverForeground);
    root.style.setProperty('--secondary', LIGHT_SURFACES.secondary);
    root.style.setProperty('--secondary-foreground', LIGHT_SURFACES.secondaryForeground);
    root.style.setProperty('--muted', LIGHT_SURFACES.muted);
    root.style.setProperty('--muted-foreground', LIGHT_SURFACES.mutedForeground);
    root.style.setProperty('--border', LIGHT_SURFACES.border);
    root.style.setProperty('--input', LIGHT_SURFACES.input);
    root.style.setProperty('--destructive', LIGHT_SURFACES.destructive);
    root.style.setProperty('--destructive-foreground', LIGHT_SURFACES.destructiveForeground);
    // Brand-derived — only accent colors
    root.style.setProperty('--primary', hexToHsl(shades['600']));
    root.style.setProperty('--primary-foreground', '0 0% 100%');
    root.style.setProperty('--accent', hexToHsl(shades['600']));
    root.style.setProperty('--accent-foreground', '0 0% 100%');
    root.style.setProperty('--ring', hexToHsl(shades['600']));
  }
}
